import { lockBodyScroll, unlockBodyScroll } from './body-scroll-lock';

const PHONE_PREFIX = '+7';
const PHONE_MASK_PLACEHOLDER = '+7 (___) ___-__-__';
const SUCCESS_AUTOCLOSE_DELAY = 5000;
const CLOSE_TRANSITION_FALLBACK_DELAY = 350;
const CALLBACK_ENDPOINT = '/local/ajax/callback.php';
const CALLBACK_YM_COUNTER_ID = 57042598;
const CALLBACK_YM_GOAL = 'obr_zv';
const DEFERRED_PHONE_INPUT_TYPES = new Set(['insertFromPaste', 'insertReplacementText']);

const getPhoneDigits = (value) =>
  value.replace(/\D/g, '').replace(/^7/, '').replace(/^8/, '').slice(0, 10);

const formatPhoneValue = (value) => {
  const digits = getPhoneDigits(value);

  if (!digits.length) {
    return '';
  }

  let result = PHONE_PREFIX;

  if (digits.length > 0) {
    result += ` (${digits.slice(0, 3)}`;
  }

  if (digits.length >= 4) {
    result += `) ${digits.slice(3, 6)}`;
  }

  if (digits.length >= 7) {
    result += `-${digits.slice(6, 8)}`;
  }

  if (digits.length >= 9) {
    result += `-${digits.slice(8, 10)}`;
  }

  return result;
};

const getCaretPositionForDigitIndex = (value, digitIndex) => {
  if (!value || digitIndex <= 0) {
    return 0;
  }

  const targetDigitCount = digitIndex + 1;
  let currentDigitCount = 0;

  for (let index = 0; index < value.length; index += 1) {
    if (!/\d/.test(value[index])) {
      continue;
    }

    currentDigitCount += 1;

    if (currentDigitCount === targetDigitCount) {
      return index + 1;
    }
  }

  return value.length;
};

export const initCallbackModal = () => {
  const modal = document.querySelector('.modal-callback');
  if (!modal) return;

  const overlay = modal.querySelector('.modal-callback__overlay');
  const dialog = modal.querySelector('.modal-callback__dialog');
  const form = modal.querySelector('.modal-callback__form');

  const nameInput = modal.querySelector('[name="name"]');
  const phoneInput = modal.querySelector('[name="phone"]');
  const agreementInput = modal.querySelector('[name="agreement"]');
  const nameField = nameInput?.closest('.modal-callback__field');
  const phoneField = phoneInput?.closest('.modal-callback__field');
  const agreementField = agreementInput?.closest('.modal-callback__checkbox');

  const openButtons = document.querySelectorAll('[data-callback-open]');
  const closeButtons = modal.querySelectorAll('[data-callback-close]');

  if (
    !overlay ||
    !dialog ||
    !form ||
    !nameInput ||
    !phoneInput ||
    !agreementInput ||
    !nameField ||
    !phoneField ||
    !agreementField
  ) {
    return;
  }

  const formControls = Array.from(form.elements);

  let isOpen = false;
  let successTimerId = null;
  let focusFrameId = 0;
  let closeResetTimerId = null;
  let closeTransitionHandler = null;
  let phoneSyncFrameId = 0;
  let isSubmitting = false;

  const setInputErrorState = (field, input, isInvalid) => {
    field.classList.toggle('modal-callback__field--error', isInvalid);
    input.setAttribute('aria-invalid', String(isInvalid));
  };

  const setAgreementErrorState = (isInvalid) => {
    agreementField.classList.toggle('modal-callback__checkbox--error', isInvalid);
    agreementInput.setAttribute('aria-invalid', String(isInvalid));
  };

  const clearErrors = () => {
    setInputErrorState(nameField, nameInput, false);
    setInputErrorState(phoneField, phoneInput, false);
    setAgreementErrorState(false);
  };

  const updatePhonePlaceholder = (shouldShowMask) => {
    phoneInput.placeholder = shouldShowMask ? PHONE_MASK_PLACEHOLDER : 'Ваш телефон';
  };

  const resetModal = () => {
    form.reset();
    phoneInput.value = '';
    updatePhonePlaceholder(false);
    clearErrors();
    modal.classList.remove('modal-callback--success');
  };

  const clearSuccessTimer = () => {
    if (successTimerId === null) {
      return;
    }

    clearTimeout(successTimerId);
    successTimerId = null;
  };

  const clearPhoneSyncFrame = () => {
    if (!phoneSyncFrameId) {
      return;
    }

    window.cancelAnimationFrame(phoneSyncFrameId);
    phoneSyncFrameId = 0;
  };

  const scheduleSuccessReset = () => {
    clearSuccessTimer();

    successTimerId = window.setTimeout(() => {
      successTimerId = null;

      if (!isOpen || !modal.classList.contains('modal-callback--success')) {
        return;
      }

      closeModal();
    }, SUCCESS_AUTOCLOSE_DELAY);
  };
  const clearCloseResetTimer = () => {
    if (closeResetTimerId === null) {
      if (closeTransitionHandler) {
        dialog.removeEventListener('transitionend', closeTransitionHandler);
        closeTransitionHandler = null;
      }

      return;
    }

    clearTimeout(closeResetTimerId);
    closeResetTimerId = null;

    if (closeTransitionHandler) {
      dialog.removeEventListener('transitionend', closeTransitionHandler);
      closeTransitionHandler = null;
    }
  };

  const scheduleResetAfterClose = () => {
    const finalizeReset = () => {
      if (closeResetTimerId !== null) {
        clearTimeout(closeResetTimerId);
        closeResetTimerId = null;
      }

      if (closeTransitionHandler) {
        dialog.removeEventListener('transitionend', closeTransitionHandler);
        closeTransitionHandler = null;
      }

      resetModal();
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== dialog || event.propertyName !== 'opacity') {
        return;
      }

      finalizeReset();
    };

    closeTransitionHandler = handleTransitionEnd;
    closeResetTimerId = window.setTimeout(finalizeReset, CLOSE_TRANSITION_FALLBACK_DELAY);
    dialog.addEventListener('transitionend', handleTransitionEnd);
  };

  const resetCaptcha = () => {
    if (typeof window.resetCallbackSmartCaptcha === 'function') {
      window.resetCallbackSmartCaptcha();
    }
  };

  const openModal = () => {
    if (isOpen) return;

    clearCloseResetTimer();
    clearPhoneSyncFrame();
    window.cancelAnimationFrame(focusFrameId);
    resetModal();
    resetCaptcha();

    isOpen = true;
    modal.classList.add('modal-callback--open');
    modal.setAttribute('aria-hidden', 'false');
    lockBodyScroll();

    focusFrameId = window.requestAnimationFrame(() => {
      nameInput.focus();
    });

    if (typeof window.initCallbackSmartCaptcha === 'function') {
      window.initCallbackSmartCaptcha();
    }
  };

  const closeModal = () => {
    if (!isOpen) return;

    isOpen = false;
    clearSuccessTimer();
    window.cancelAnimationFrame(focusFrameId);
    clearCloseResetTimer();
    clearPhoneSyncFrame();
    resetCaptcha();

    modal.classList.remove('modal-callback--open');
    modal.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();

    scheduleResetAfterClose();
  };

  const validateName = () => {
    const value = nameInput.value.trim();

    if (!value) {
      setInputErrorState(nameField, nameInput, true);
      return false;
    }

    setInputErrorState(nameField, nameInput, false);
    return true;
  };

  const validatePhone = () => {
    const digits = phoneInput.value.replace(/\D/g, '');

    if (!phoneInput.value.trim()) {
      setInputErrorState(phoneField, phoneInput, true);
      return false;
    }

    if (digits.length !== 11 || !phoneInput.value.startsWith(PHONE_PREFIX)) {
      setInputErrorState(phoneField, phoneInput, true);
      return false;
    }

    setInputErrorState(phoneField, phoneInput, false);
    return true;
  };

  const validateAgreement = () => {
    if (!agreementInput.checked) {
      setAgreementErrorState(true);
      return false;
    }

    setAgreementErrorState(false);
    return true;
  };

  const setSubmittingState = (submitting) => {
    isSubmitting = submitting;
    form.classList.toggle('modal-callback__form--submitting', submitting);

    formControls.forEach((control) => {
      control.disabled = submitting;
    });
  };

  const trackCallbackSuccess = () => {
    if (typeof window.ym !== 'function') {
      return;
    }

    window.ym(CALLBACK_YM_COUNTER_ID, 'reachGoal', CALLBACK_YM_GOAL);
  };

  openButtons.forEach((btn) => {
    btn.addEventListener('click', openModal);
  });

  closeButtons.forEach((btn) => {
    btn.addEventListener('click', closeModal);
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeModal();
    }
  });

  dialog.addEventListener('click', (e) => {
    e.stopPropagation();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) {
      closeModal();
    }
  });

  nameInput.addEventListener('input', validateName);

  const showPhoneMask = () => {
    if (!phoneInput.value) {
      updatePhonePlaceholder(true);
    }
  };

  const hidePhoneMask = () => {
    if (!phoneInput.value) {
      updatePhonePlaceholder(false);
    }
  };

  const applyPhoneValue = (preserveCaret = false) => {
    const activeElement = document.activeElement;
    const isPhoneFocused = activeElement === phoneInput;
    const selectionStart =
      preserveCaret && isPhoneFocused && typeof phoneInput.selectionStart === 'number'
        ? phoneInput.selectionStart
        : null;
    const digitsBeforeCaret =
      selectionStart === null
        ? null
        : getPhoneDigits(phoneInput.value.slice(0, selectionStart)).length;

    phoneInput.value = formatPhoneValue(phoneInput.value);
    validatePhone();

    if (phoneInput.value) {
      updatePhonePlaceholder(true);
    } else {
      updatePhonePlaceholder(isPhoneFocused);
    }

    if (digitsBeforeCaret === null || !isPhoneFocused) {
      return;
    }

    const caretPosition = getCaretPositionForDigitIndex(phoneInput.value, digitsBeforeCaret);
    phoneInput.setSelectionRange(caretPosition, caretPosition);
  };

  const syncPhoneInput = (event) => {
    clearPhoneSyncFrame();

    const shouldDeferSync =
      event?.type === 'input' &&
      (!event.inputType || DEFERRED_PHONE_INPUT_TYPES.has(event.inputType));

    if (shouldDeferSync) {
      phoneSyncFrameId = window.requestAnimationFrame(() => {
        phoneSyncFrameId = 0;
        applyPhoneValue(false);
      });

      return;
    }

    applyPhoneValue(event?.type === 'input');
  };

  phoneInput.addEventListener('focus', showPhoneMask);
  phoneInput.addEventListener('mouseenter', showPhoneMask);
  phoneInput.addEventListener('mouseleave', hidePhoneMask);
  phoneInput.addEventListener('input', syncPhoneInput);
  phoneInput.addEventListener('change', syncPhoneInput);
  phoneInput.addEventListener('blur', () => {
    syncPhoneInput();
    hidePhoneMask();
  });

  agreementInput.addEventListener('change', validateAgreement);

  form.addEventListener('submit', (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    const isValid = [validateName(), validatePhone(), validateAgreement()].every(Boolean);

    if (!isValid) return;

    const body = new URLSearchParams(new FormData(form));

    setSubmittingState(true);

    const sendForm = (token) => {
      if (token) {
        body.set('smart-token', token);
      }

      return fetch(CALLBACK_ENDPOINT, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Callback request failed with status ${response.status}`);
          }

          return response.text();
        })
        .then(() => {
          modal.classList.add('modal-callback--success');
          trackCallbackSuccess();
          scheduleSuccessReset();
          resetCaptcha();
        });
    };

    const captchaPromise =
      typeof window.executeCallbackSmartCaptcha === 'function'
        ? (window.initCallbackSmartCaptcha?.(), window.executeCallbackSmartCaptcha())
        : Promise.resolve('');

    captchaPromise
      .then(sendForm)
      .catch((error) => {
        console.error(error);
        if (typeof window.resetCallbackSmartCaptcha === 'function') {
          window.resetCallbackSmartCaptcha();
        }
      })
      .finally(() => {
        setSubmittingState(false);
      });
  });
};
